import { useMemo, useRef, useState, type FormEvent } from 'react';
import type { ProjectView } from '@machs/shared';
import { useSync } from '../lib/sync';
import { usePlan } from '../lib/plan';
import { api } from '../lib/api';
import { Icon } from '../lib/icons';
import { avatarGradient, initials } from '../lib/format';
import { imageUrl, queueImageFile } from '../lib/images';
import { Dialog, ImagePicker, Picker, useToast } from './ui';

/** Aktive Geschäfte; das Hauptgeschäft zuerst. */
export function useBusinesses() {
  const { client, version } = useSync();
  return useMemo(
    () => [...client.data.business.values()].filter((b) => !b.archivedAt).sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name, 'de')),
    [client, version],
  );
}

/** Je Dialog eine Unteraufgabe anlegen und danach schließen. */
export function SubtaskDialog({ parentId, onClose }: { parentId: string; onClose: () => void }) {
  const { client, me } = useSync();
  const plan = usePlan();
  const parent = client.data.task.get(parentId);
  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const toast = useToast();
  if (!parent) return null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    const t = title.trim();
    if (submitting.current || !t || !plan.more('openTasks')) return;
    submitting.current = true; setSaving(true);
    try {
      await client.saveTask({ projectId: parent!.projectId, title: t, parentId: parent!.id, ...(assignee && { assigneeIds: [assignee] }) });
      onClose();
    } catch (error) { toast(error instanceof Error ? error.message : 'Unteraufgabe konnte nicht angelegt werden'); }
    finally { submitting.current = false; setSaving(false); }
  }

  return (
    <Dialog title="Unteraufgabe anlegen" onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <p className="m-0 -mt-2 text-[13.5px] text-[var(--muted)]">zu „{parent.title}“ – mit eigenem Chat, im selben Projekt</p>
        <label className="field">
          Titel
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Preisliste gegenlesen" maxLength={200} autoFocus />
        </label>
        {me.members.length > 1 && (
          <div className="field">
            Zuständig (optional)
          <div className="flex flex-wrap gap-1.5">
            {me.members.map((m) => (
              <button key={m.userId} type="button" className="chip" aria-pressed={assignee === m.userId} onClick={() => setAssignee((a) => (a === m.userId ? null : m.userId))}>
                {assignee === m.userId && <Icon name="check" size={13} />}
                {m.userId === me.user.id ? 'Ich' : m.name.split(' ')[0]}
              </button>
            ))}
          </div>
          </div>
        )}
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" disabled={saving || !title.trim()}>{saving ? 'Wird angelegt …' : 'Anlegen'}</button>
        </div>
      </form>
    </Dialog>
  );
}

export function TaskDialog({ defaultBusinessId, defaultProjectId, onClose, onCreated }: {
  defaultBusinessId: string | null;
  defaultProjectId: string | null;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { client, version } = useSync();
  const plan = usePlan();
  const [projectId, setProjectId] = useState<string | null>(defaultProjectId);
  const [title, setTitle] = useState('');
  const [problem, setProblem] = useState('');

  const projects = useMemo(
    () => [...client.data.project.values()]
      .filter((p) => !p.archivedAt && (!defaultBusinessId || p.businessId === defaultBusinessId))
      .sort((a, b) => Number(b.isInternal) - Number(a.isInternal) || a.name.localeCompare(b.name, 'de')),
    [client, version, defaultBusinessId],
  );
  const effectiveProject = projectId && projects.some((p) => p.id === projectId) ? projectId : projects.length === 1 ? projects[0]!.id : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!effectiveProject || !title.trim()) return;
    if (!plan.more('openTasks')) return;
    const id = await client.saveTask({ projectId: effectiveProject, title: title.trim() });
    if (problem.trim()) await client.sendMessage({ taskId: id, kind: 'text', body: problem.trim() });
    onCreated(id);
    onClose();
  }

  return (
    <Dialog title="Neue Aufgabe" onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <label className="field">Worum geht es?<input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="z. B. Warenkorb zeigt falsche Preise" maxLength={200} required /></label>
        <Picker label="Projekt" value={effectiveProject} onChange={setProjectId} options={projects.map((p) => ({ value: p.id, label: p.name }))} placeholder="Projekt wählen" />
        <label className="field">
          Problem beschreiben (optional)
          <textarea className="input min-h-[92px] py-3" value={problem} onChange={(e) => setProblem(e.target.value)} placeholder="Fotos, Videos und Sprachnachrichten kannst du gleich im Chat ergänzen." />
        </label>
        <div className="mt-2 flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" disabled={!effectiveProject || !title.trim()}>Aufgabe anlegen</button>
        </div>
      </form>
    </Dialog>
  );
}

/** Projekt anlegen oder bearbeiten. Ein Projekt kann ein Kunde oder ein internes Vorhaben sein. */
export function ProjectDialog({ id, defaultBusinessId, onClose, onSaved }: { id?: string; defaultBusinessId: string | null; onClose: () => void; onSaved: (id: string) => void }) {
  const { client } = useSync();
  const plan = usePlan();
  const businesses = useBusinesses();
  const existing = id ? client.data.project.get(id) : undefined;
  const [name, setName] = useState(existing?.name ?? '');
  const [businessId, setBusinessId] = useState<string | null>(existing?.businessId ?? defaultBusinessId ?? businesses[0]?.id ?? null);
  const [image, setImage] = useState<{ file: File; url: string } | null | undefined>(undefined);
  const [view, setView] = useState<ProjectView>(existing?.view ?? 'liste');
  const preview = image === undefined ? imageUrl(existing?.imageMediaId) : image?.url ?? null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || !businessId) return;
    // Neues Projekt zählt gegen die Tarifgrenze
    if (!existing && !plan.more('projects')) return;
    const imageMediaId = image === undefined ? undefined : image ? await queueImageFile(client, image.file) : null;
    const pid = await client.saveProject({ id, businessId, name: name.trim(), imageMediaId, view, archivedAt: existing?.archivedAt ?? null });
    onSaved(pid);
    onClose();
  }

  return (
    <Dialog title={id ? 'Projekt bearbeiten' : 'Projekt anlegen'} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        <ImagePicker
          label="Projektbild oder Logo"
          square
          preview={preview}
          fallback={{ name: initials(name || '?'), gradient: avatarGradient(id ?? name) }}
          onPick={(file) => setImage({ file, url: URL.createObjectURL(file) })}
          onRemove={() => setImage(null)}
        />
        <label className="field">
          Name
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Bäckerei Sonnenkorn oder Relaunch Website" required maxLength={120} />
        </label>
        {businesses.length > 1 && !existing?.isInternal && (
          <Picker label="Geschäft" value={businessId} onChange={setBusinessId} options={businesses.map((b) => ({ value: b.id, label: b.name }))} />
        )}
        <Picker
          label="Ansicht der Aufgaben"
          value={view}
          onChange={(v) => setView(v as ProjectView)}
          options={[
            { value: 'liste', label: 'Liste — Aufgaben als Chats nebeneinander' },
            { value: 'board', label: 'Board — Aufgaben als Karten in Spalten' },
          ]}
        />
        <div className="mt-2 flex items-center justify-end gap-2">
          {id && existing && !existing.isInternal && (
            <button type="button" className="btn btn-danger mr-auto" onClick={async () => {
              await client.saveProject({ id, businessId: existing.businessId, name: existing.name, archivedAt: new Date().toISOString() });
              onClose();
            }}>
              <Icon name="archive" size={18} />Archivieren
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" disabled={!name.trim()}>{id ? 'Speichern' : 'Projekt anlegen'}</button>
        </div>
      </form>
    </Dialog>
  );
}

// Farben für Geschäfte: deutlich unterscheidbar, alle mit weißer Schrift lesbar
const COLORS = ['#6c63ff', '#e0632f', '#0e9b76', '#c2185b', '#0f74d8', '#7352d6'] as const;

export function BusinessDialog({ id, onClose, onSaved, onDeleted }: {
  id?: string;
  onClose: () => void;
  onSaved: (id: string) => void;
  onDeleted?: () => void;
}) {
  const { client, me } = useSync();
  const plan = usePlan();
  const existing = id ? client.data.business.get(id) : undefined;
  const [name, setName] = useState(existing?.name ?? '');
  const [color, setColor] = useState(existing?.color ?? COLORS[0]!);
  // Löschen in zwei Schritten: erst die Folgen zeigen, dann den Namen tippen
  const [removing, setRemoving] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [contents, setContents] = useState<{ projects: number; tasks: number; messages: number; media: number; shares: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isAdmin = me.role === 'owner' || me.role === 'admin';
  const isDefault = !!existing?.isDefault;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (!existing && !plan.more('businesses')) return;
    const bid = await client.saveBusiness({ id, name: name.trim(), color });
    // Das Projekt „Intern" des neuen Geschäfts legt der Server an
    onSaved(bid);
    onClose();
  }

  async function startRemove() {
    setRemoving(true);
    setContents(null);
    try {
      const r = await api<{ contents: typeof contents }>(`/settings/business/${id}/contents`);
      setContents(r.contents);
    } catch {
      setContents(null);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api(`/settings/business/${id}`, { method: 'DELETE', json: { confirmName } });
      await client.flush();
      onDeleted?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Löschen nicht möglich');
    } finally {
      setBusy(false);
    }
  }

  if (removing) {
    const total = contents ? contents.tasks + contents.messages + contents.media : 0;
    return (
      <Dialog title={`${existing?.name} löschen`} onClose={onClose}>
        <div className="flex flex-col gap-4">
          <div className="check">
            <Icon name="alert" size={18} />
            <span>
              Das Geschäft wird mit allen Inhalten gelöscht. Das lässt sich nicht zurücknehmen.
            </span>
          </div>
          {contents === null ? (
            <p className="m-0 text-[14px] text-[var(--muted)]">Inhalte werden gezählt …</p>
          ) : (
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[14px]">
              <li>{contents.projects} {contents.projects === 1 ? 'Projekt' : 'Projekte'}</li>
              <li>{contents.tasks} {contents.tasks === 1 ? 'Aufgabe' : 'Aufgaben'}</li>
              <li>{contents.messages} {contents.messages === 1 ? 'Nachricht' : 'Nachrichten'}, {contents.media} {contents.media === 1 ? 'Medium' : 'Medien'}</li>
              {contents.shares > 0 && <li>{contents.shares} {contents.shares === 1 ? 'Freigabe' : 'Freigaben'} für Externe werden ungültig</li>}
              {total === 0 && <li className="text-[var(--muted)]">Bisher keine Inhalte.</li>}
            </ul>
          )}
          <label className="field">
            Zum Bestätigen „{existing?.name}“ eingeben
            <input className="input" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={existing?.name} autoFocus />
          </label>
          {error && <div className="check" role="alert"><Icon name="alert" size={16} />{error}</div>}
          <div className="mt-1 flex justify-end gap-2">
            <button type="button" className="btn btn-ghost" onClick={() => (setRemoving(false), setConfirmName(''))}>Abbrechen</button>
            <button
              type="button"
              className="btn"
              style={{ background: 'var(--problem)', color: '#fff' }}
              disabled={busy || confirmName.trim() !== existing?.name}
              onClick={() => void remove()}
            >
              {busy ? 'Wird gelöscht …' : 'Endgültig löschen'}
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={id ? 'Geschäft bearbeiten' : 'Weiteres Geschäft anlegen'} onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={submit}>
        {!id && (
          <p className="m-0 text-[14px] text-[var(--muted)]">
            Für getrennte Geschäftsbereiche mit eigenen Projekten und eigenem KI-Key. Oben in der Seitenleiste kannst du dann zwischen den Geschäften wechseln.
          </p>
        )}
        <label className="field">Name des Geschäfts<input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} /></label>
        <div className="field">
          <span>Farbe</span>
          <div className="flex flex-wrap gap-2">
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                className="h-9 w-9 rounded-full"
                style={{ background: c, boxShadow: c === color ? '0 0 0 3px var(--bg), 0 0 0 5px var(--text)' : 'none' }}
                aria-label={`Farbe ${c}`}
                aria-pressed={c === color}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
          <p className="m-0 text-[13px] text-[var(--muted)]">Die Farbe färbt Knöpfe und Akzente, solange dieses Geschäft aktiv ist.</p>
        </div>
        <div className="mt-2 flex flex-wrap justify-end gap-2">
          {id && isAdmin && !isDefault && (
            <button type="button" className="btn btn-ghost mr-auto" style={{ color: 'var(--problem)' }} onClick={() => void startRemove()}>
              <Icon name="trash" size={17} />Geschäft löschen
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button className="btn btn-primary" disabled={!name.trim()}>{id ? 'Speichern' : 'Geschäft anlegen'}</button>
        </div>
      </form>
    </Dialog>
  );
}
